import { useRef } from "react";
import { RoundedBox } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { type ValveState } from "@reagent/sim";
import { PALETTE } from "../palette";
import { useToonMaterial } from "../materials/useToonMaterial";
import { Chassis, Dial, GlassTube, ToggleSwitch } from "./parts";
import { useGameStore, nextValveState } from "../../app/gameStore";

/**
 * The ten deployable hardware kinds (`docs/specs/0001-hardware-node-taxonomy.md`).
 * All are procedural (no DCC assets yet) and share the `parts` vocabulary so
 * they read as one 1970s product line. Each is authored to sit within a 1x1
 * tile footprint, origin at tile center, resting on the board surface at y=0.
 *
 * `ThermalRegulator`, `Separator`, `PhaseConverter`, and `Incubator` are
 * placeholder meshes for the four newly-declared, currently-inert categories:
 * built from the same primitives as everything else, distinct in silhouette
 * and colour so the mock board's 10 kinds read apart by eye, but with no
 * hazard-tell state to visualize yet (they carry plain `PieceProps`).
 */

export interface PieceProps {
  /** 0..1 drives dials/fill so a piece can look "busy" during a run. */
  activity?: number;
  /** Hazard heat 0..1 — tints the chassis toward the critical accent. */
  heat?: number;
  /** Supply level 0..1 for capped Premium tanks; overrides activity-driven fill. */
  supplyLevel?: number | null;
}

/** Precursor stream source. A big bubbling reservoir with a feed valve. */
export function InputTank({ activity = 0.4, supplyLevel = null }: PieceProps) {
  // Show supply remaining for capped Premium tanks; otherwise activity-driven.
  const fill = supplyLevel !== null ? 0.2 + supplyLevel * 0.6 : 0.55 + activity * 0.3;
  return (
    <group>
      <Chassis width={0.78} height={0.22} depth={0.78} position={[0, 0.11, 0]} />
      <GlassTube position={[0, 0.5, 0]} radius={0.2} height={0.56} fill={fill} fluidColor={PALETTE.fluidSkyBlue} />
      <Dial position={[0.26, 0.23, 0.3]} value={activity} />
      <ToggleSwitch position={[-0.24, 0.23, 0.3]} on={activity > 0.2} />
    </group>
  );
}

/** Combines inbound reagents. Chunky body with a spinning agitator on top. */
export function Reactor({ activity = 0.5, heat = 0 }: PieceProps) {
  const agitatorRef = useRef<THREE.Group>(null);
  const bodyColor = heat > 0.6 ? PALETTE.hazardCritical : heat > 0.3 ? PALETTE.hazardWarning : PALETTE.mustard;
  const agitatorMaterial = useToonMaterial(PALETTE.chassisGreyDark);

  useFrame((_, delta) => {
    if (agitatorRef.current) agitatorRef.current.rotation.y += delta * (1 + activity * 8);
  });

  return (
    <group>
      <Chassis width={0.82} height={0.34} depth={0.82} color={bodyColor} position={[0, 0.17, 0]} />
      <GlassTube position={[0, 0.55, 0]} radius={0.17} height={0.4} fill={activity} fluidColor={PALETTE.fluidMagenta} />
      <group ref={agitatorRef} position={[0, 0.8, 0]}>
        <mesh castShadow>
          <boxGeometry args={[0.3, 0.04, 0.06]} />
          <primitive object={agitatorMaterial} attach="material" />
        </mesh>
      </group>
      <Dial position={[0.28, 0.35, 0.32]} value={activity} />
    </group>
  );
}

/** Splits one inbound stream into two. Wedge-shaped with two outlet spouts. */
export function FlowMover({
  activity = 0.3,
  valveState = null,
  outgoingEdgeIds = [],
  nodeId = "",
}: PieceProps & { valveState?: ValveState | null; outgoingEdgeIds?: readonly string[]; nodeId?: string }) {
  const spoutMaterial = useToonMaterial(PALETTE.chassisGreyDark);
  const groupRef = useRef<THREE.Group>(null);

  // Handle valve state clicks (spec 0024).
  const handleValveClick = (e: { stopPropagation: () => void }) => {
    const state = useGameStore.getState();
    if (state.snapshot.phase !== "play" || state.pendingPipe || !nodeId) return;
    e.stopPropagation();
    const nextState = nextValveState(valveState, outgoingEdgeIds);
    state.setValveAt(nodeId, nextState);
  };

  return (
    <group ref={groupRef} onClick={handleValveClick}>
      <Chassis width={0.76} height={0.3} depth={0.76} color={PALETTE.sage} position={[0, 0.15, 0]} />
      <mesh position={[0, 0.4, 0]} castShadow>
        <coneGeometry args={[0.26, 0.28, 4]} />
        <primitive object={spoutMaterial} attach="material" />
      </mesh>
      <GlassTube position={[0.24, 0.36, 0]} radius={0.07} height={0.2} fill={activity} fluidColor={PALETTE.fluidMintGreen} />
      <GlassTube position={[-0.24, 0.36, 0]} radius={0.07} height={0.2} fill={activity} fluidColor={PALETTE.fluidMintGreen} />
      {/* Valve state indicator (spec 0024). */}
      <ValveStateIndicator valveState={valveState} />
    </group>
  );
}

/**
 * Catalytic bed. Doubles yield but degrades on impure feed, so the
 * catalyst bed is visible: it desaturates as catalystHealth drops.
 */
export function CatalyticBed({ activity = 0.5, heat = 0 }: PieceProps & { catalystHealth?: number }) {
  const bedMaterial = useToonMaterial(heat > 0.5 ? PALETTE.hazardWarning : PALETTE.fluidAmber, {
    emissive: PALETTE.fluidAmber,
    emissiveIntensity: 0.25,
  });
  return (
    <group>
      <Chassis width={0.8} height={0.26} depth={0.8} color={PALETTE.terracotta} position={[0, 0.13, 0]} />
      {/* Catalyst bed: a stack of pellets behind glass. */}
      <mesh position={[0, 0.42, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.15, 0.28, 12]} />
        <primitive object={bedMaterial} attach="material" />
      </mesh>
      <GlassTube position={[0, 0.45, 0]} radius={0.2} height={0.44} fill={0} />
      <Dial position={[0.27, 0.27, 0.3]} value={activity} color={PALETTE.creamDark} />
    </group>
  );
}

/** Buffer vessel for parking excess fluid — the pressure-relief play. */
export function FlowBuffer({ activity = 0.6 }: PieceProps) {
  return (
    <group>
      <Chassis width={0.72} height={0.18} depth={0.72} position={[0, 0.09, 0]} />
      <GlassTube position={[0, 0.52, 0]} radius={0.24} height={0.66} fill={activity} fluidColor={PALETTE.fluidAmber} />
      <ToggleSwitch position={[0.28, 0.19, 0.28]} on={activity > 0.8} />
    </group>
  );
}

/** Collection hopper for target output; sits on the board's right edge. */
export function ProductHopper({ activity = 0.2 }: PieceProps) {
  const hopperMaterial = useToonMaterial(PALETTE.chassisGrey);
  const fillMaterial = useToonMaterial(PALETTE.fluidMintGreen, {
    emissive: PALETTE.fluidMintGreen,
    emissiveIntensity: 0.3,
  });
  return (
    <group>
      <RoundedBox args={[0.8, 0.16, 0.8]} radius={0.05} smoothness={3} position={[0, 0.08, 0]} castShadow receiveShadow>
        <primitive object={hopperMaterial} attach="material" />
      </RoundedBox>
      {/* Funnel mouth. Closed, not open-ended: an open cylinder is backface
          culled from this camera angle and reads as a dark hollow. */}
      <mesh position={[0, 0.38, 0]} castShadow>
        <cylinderGeometry args={[0.34, 0.18, 0.44, 14]} />
        <primitive object={hopperMaterial} attach="material" />
      </mesh>
      {/* Collected product glowing in the throat of the hopper. */}
      <mesh position={[0, 0.62, 0]}>
        <cylinderGeometry args={[0.3, 0.3, 0.06 + activity * 0.16, 14]} />
        <primitive object={fillMaterial} attach="material" />
      </mesh>
    </group>
  );
}

/**
 * Waste buyer (byproduct collector). Similar funnel-and-base shape as
 * ProductHopper but with a wider, squatter body and angled intake slot
 * rather than a smooth funnel, tinted terracotta to read as a distinct
 * hardware identity. Distinct in silhouette: wider base, ribbed top
 * intake reads apart from ProductHopper's smooth inverted cone even
 * without colour.
 */
export function WasteBuyer({ activity = 0.2 }: PieceProps) {
  const baseMaterial = useToonMaterial(PALETTE.terracotta);
  const fillMaterial = useToonMaterial(PALETTE.fluidAmber, {
    emissive: PALETTE.fluidAmber,
    emissiveIntensity: 0.25,
  });
  return (
    <group>
      {/* Wider base than ProductHopper, squat profile. */}
      <RoundedBox args={[0.9, 0.12, 0.9]} radius={0.04} smoothness={3} position={[0, 0.06, 0]} castShadow receiveShadow>
        <primitive object={baseMaterial} attach="material" />
      </RoundedBox>
      {/* Angled intake slot rather than smooth funnel — tilted opening to read
          as "material being shunted sideways" rather than falling straight down. */}
      <mesh position={[0, 0.28, 0.12]} rotation={[0.3, 0, 0]} castShadow>
        <coneGeometry args={[0.28, 0.36, 6]} />
        <primitive object={baseMaterial} attach="material" />
      </mesh>
      {/* Collected byproduct in amber tint — not the objective, but still material. */}
      <mesh position={[0, 0.42, 0]}>
        <cylinderGeometry args={[0.26, 0.26, 0.06 + activity * 0.12, 12]} />
        <primitive object={fillMaterial} attach="material" />
      </mesh>
    </group>
  );
}

/**
 * Thermal Regulator (category 2, inert). A squat heat-exchanger block: a
 * low chassis ringed by finned coils, alternating warm and cool tint so the
 * silhouette reads as "temperature control" before any behaviour is wired
 * up. Distinct from every other piece by being the only low, ringed block.
 */
export function ThermalRegulator({ activity = 0.4 }: PieceProps) {
  const warmFinMaterial = useToonMaterial(PALETTE.mustard);
  const coolFinMaterial = useToonMaterial(PALETTE.fluidSkyBlue, {
    emissive: PALETTE.fluidSkyBlue,
    emissiveIntensity: 0.15,
  });
  const finCount = 8;
  const finRadius = 0.32;

  return (
    <group>
      <Chassis width={0.84} height={0.24} depth={0.84} color={PALETTE.chassisGreyDark} position={[0, 0.12, 0]} />
      {/* Coil ring: half warm, half cool, so the piece reads as regulating
          between the two rather than heating or cooling outright. */}
      <group position={[0, 0.34, 0]}>
        {Array.from({ length: finCount }, (_, i) => {
          const angle = (i / finCount) * Math.PI * 2;
          const isWarm = i < finCount / 2;
          return (
            <mesh
              key={i}
              position={[Math.cos(angle) * finRadius, 0, Math.sin(angle) * finRadius]}
              castShadow
            >
              <cylinderGeometry args={[0.05, 0.05, 0.18, 8]} />
              <primitive object={isWarm ? warmFinMaterial : coolFinMaterial} attach="material" />
            </mesh>
          );
        })}
      </group>
      <Dial position={[0, 0.46, 0]} value={activity} color={PALETTE.cream} />
    </group>
  );
}

/**
 * Separator (category 3, inert). A tall, narrow hexagonal cone over two
 * offset outlet stubs, previewing its eventual Main/Waste split without any
 * routing logic behind it yet: the two stubs are tinted product (mint) and
 * waste (amber) rather than matched, unlike Flow Mover's identical pair.
 * Taller and narrower than Flow Mover's cone, and offset diagonally rather
 * than mirrored front-on, so the two read apart even in silhouette.
 */
export function Separator({ activity = 0.3 }: PieceProps) {
  const coneMaterial = useToonMaterial(PALETTE.chassisGreyDark);

  return (
    <group>
      <Chassis width={0.74} height={0.26} depth={0.74} color={PALETTE.sageDark} position={[0, 0.13, 0]} />
      <mesh position={[0, 0.52, 0]} castShadow>
        <coneGeometry args={[0.2, 0.44, 6]} />
        <primitive object={coneMaterial} attach="material" />
      </mesh>
      <GlassTube position={[0.28, 0.32, 0.16]} radius={0.06} height={0.2} fill={activity} fluidColor={PALETTE.fluidMintGreen} />
      <GlassTube position={[-0.28, 0.32, -0.16]} radius={0.06} height={0.2} fill={activity} fluidColor={PALETTE.fluidAmber} />
    </group>
  );
}

/**
 * Phase Converter (category 4, inert). A tall, narrow distillation column:
 * by far the tallest silhouette in the kit, capped with a small condenser
 * cone where a Flow Mover or Catalytic Bed would instead top out in a stubby
 * spout or pellet stack.
 */
export function PhaseConverter({ activity = 0.35 }: PieceProps) {
  const capMaterial = useToonMaterial(PALETTE.mustardDark);

  return (
    <group>
      <Chassis width={0.58} height={0.2} depth={0.58} color={PALETTE.chassisGreyDark} position={[0, 0.1, 0]} />
      <GlassTube position={[0, 0.62, 0]} radius={0.12} height={0.9} fill={activity} fluidColor={PALETTE.fluidSkyBlue} />
      {/* Condenser cap: the vapor takeoff at the head of the column. */}
      <mesh position={[0, 1.1, 0]} castShadow>
        <coneGeometry args={[0.1, 0.14, 10]} />
        <primitive object={capMaterial} attach="material" />
      </mesh>
    </group>
  );
}

/**
 * Incubator (category 6, inert). A boxy, enclosed chamber, taller and more
 * fully-sided than any open vessel in the kit, with a glowing viewing window
 * hinting at whatever is being held inside rather than a tube's open fill.
 */
export function Incubator({ activity = 0.4 }: PieceProps) {
  const windowMaterial = useToonMaterial(PALETTE.fluidMintGreen, {
    emissive: PALETTE.fluidMintGreen,
    emissiveIntensity: 0.2 + activity * 0.2,
  });

  return (
    <group>
      <Chassis width={0.86} height={0.52} depth={0.86} color={PALETTE.creamDark} position={[0, 0.26, 0]} />
      {/* Viewing window: flush against the front face, glowing with whatever
          the chamber is holding. */}
      <mesh position={[0, 0.3, 0.44]} castShadow>
        <boxGeometry args={[0.32, 0.24, 0.03]} />
        <primitive object={windowMaterial} attach="material" />
      </mesh>
      <ToggleSwitch position={[0.3, 0.06, 0.4]} on={activity > 0.2} />
    </group>
  );
}

/**
 * Vent sink (spec 0032). A flare/exhaust outlet for disposing of unwanted
 * material without valorizing it: narrow cylindrical intake base flaring to a
 * wide outlet at the top, silhouette reads as "waste disposal" at board zoom.
 * Tinted mustard to fit the warm industrial palette and read as distinct from
 * the other I/O kinds.
 */
export function Vent({ activity = 0.2 }: PieceProps) {
  const pipeMaterial = useToonMaterial(PALETTE.mustardDark);
  // Flare glows hotter with recent activity, same "something is happening"
  // cue Incubator's viewing window and WasteBuyer's fill level give.
  const flareMaterial = useToonMaterial(PALETTE.mustard, {
    emissive: PALETTE.mustard,
    emissiveIntensity: 0.15 + activity * 0.25,
  });

  return (
    <group>
      {/* Intake base: a narrow cylindrical pipe. */}
      <mesh position={[0, 0.15, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.12, 0.18, 8]} />
        <primitive object={pipeMaterial} attach="material" />
      </mesh>
      {/* Flare outlet: wide inverted cone at the top, reading as active exhaust. */}
      <mesh position={[0, 0.42, 0]} castShadow>
        <coneGeometry args={[0.32, 0.3, 10]} />
        <primitive object={flareMaterial} attach="material" />
      </mesh>
    </group>
  );
}

/**
 * Valve state indicator (spec 0024). A three-position indicator showing the
 * flowMover's routing setting: open (default round-robin), closed (blocks flow),
 * or routed to a specific edge. The shape is always readable, never colour-only
 * (.claude/rules/ui-and-accessibility.md).
 */
function ValveStateIndicator({ valveState }: { valveState?: ValveState | null }) {
  const indicatorMaterial = useToonMaterial(PALETTE.mustard);
  const trackMaterial = useToonMaterial(PALETTE.chassisGreyDark);

  // Determine which position the indicator should be at.
  // Open (null or "open"): position 0 (right, ~0°)
  // Closed: position 1 (back-left, ~120°)
  // Route: position 2 (front-left, ~240°)
  let position = 0;
  if (valveState === null || (typeof valveState === "object" && valveState.kind === "open")) {
    position = 0;
  } else if (typeof valveState === "object" && valveState.kind === "closed") {
    position = 1;
  } else if (typeof valveState === "object" && valveState.kind === "route") {
    position = 2;
  }

  const radius = 0.11;
  const positions = [
    [radius, 0, 0], // Open: right
    [-radius * 0.5, 0, radius * 0.866], // Closed: back-left
    [-radius * 0.5, 0, -radius * 0.866], // Route: front-left
  ] as const;

  return (
    <group position={[0, 0.48, 0]}>
      {/* Track: three small dimples where the indicator can sit. */}
      {positions.map((pos, i) => (
        <mesh key={`track-${i}`} position={pos as [number, number, number]}>
          <sphereGeometry args={[0.04, 6, 6]} />
          <primitive object={trackMaterial} attach="material" />
        </mesh>
      ))}
      {/* Indicator: moves to the current valve state position. */}
      <mesh position={positions[position] as [number, number, number]}>
        <sphereGeometry args={[0.05, 8, 8]} />
        <primitive object={indicatorMaterial} attach="material" />
      </mesh>
    </group>
  );
}
